import { useState, useEffect } from 'react';
import { associateAgent, createAndAssociateAgent } from '../../services/callService';
import { searchClients } from '../../services/clientService';
import { searchProperties, getPropertyById } from '../../services/propertyService';
import { searchAgents } from '../../services/agentService';
import { searchBusinesses } from '../../services/businessService';
import { searchProfiles } from '../../services/profileService';
import type { Client } from '../../types/client';
import type { Property } from '../../types/property';
import type { Agent } from '../../types/agent';
import type { Profile } from '../../types/profile';

interface AssociateAgentModalProps {
  callId: string;
  callPropertyId: string | null;
  onClose: () => void;
  onSuccess: () => void;
}

const TEAM_ROLES = ['owner', 'admin', 'user'];

export function AssociateAgentModal({ callId, callPropertyId, onClose, onSuccess }: AssociateAgentModalProps) {
  const [clients, setClients] = useState<Client[]>([]);
  const [properties, setProperties] = useState<Property[]>([]);
  const [agents, setAgents] = useState<Agent[]>([]);
  const [profiles, setProfiles] = useState<Profile[]>([]);
  const [selectedClientId, setSelectedClientId] = useState('');
  const [selectedPropertyId, setSelectedPropertyId] = useState('');
  const [selectedValue, setSelectedValue] = useState('');
  const [createNew, setCreateNew] = useState(false);
  const [newAgentName, setNewAgentName] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [locked, setLocked] = useState(false);

  // Load clients on mount
  useEffect(() => {
    searchClients({}, { pageSize: 1000, orderBy: 'name', order: 'asc' }).then(setClients);
  }, []);

  // If callPropertyId is set, pre-populate client + property and lock them
  useEffect(() => {
    if (!callPropertyId) return;
    getPropertyById(callPropertyId).then((prop) => {
      setSelectedClientId(prop.clientId);
      setSelectedPropertyId(prop.id);
      setProperties([prop]);
      setLocked(true);
    });
  }, [callPropertyId]);

  // Load properties when client changes (skip when locked)
  useEffect(() => {
    if (locked) return;
    if (!selectedClientId) {
      setProperties([]);
      return;
    }
    searchProperties({ clientId: selectedClientId }, { pageSize: 1000, orderBy: 'name', order: 'asc' }).then(setProperties);
  }, [selectedClientId, locked]);

  // Load agents and team profiles when client changes
  useEffect(() => {
    if (!selectedClientId) {
      setAgents([]);
      setProfiles([]);
      return;
    }
    searchAgents({ clientId: selectedClientId }, { pageSize: 1000, orderBy: 'name', order: 'asc' }).then(setAgents);
    searchBusinesses({ clientId: selectedClientId }, { pageSize: 1000 }).then(async (businesses) => {
      const allProfiles: Profile[] = [];
      for (const biz of businesses) {
        const bizProfiles = await searchProfiles(
          { persona: 'business', businessId: biz.id },
          { pageSize: 1000, expand: 'agent' },
        );
        allProfiles.push(...bizProfiles);
      }
      setProfiles(allProfiles.filter((p) => p.roles?.some((r) => TEAM_ROLES.includes(r))));
    });
  }, [selectedClientId]);

  // Filter agents to only those without a profileId (those with profileId appear in team members)
  const unlinkedAgents = agents.filter((a) => !a.profileId);

  const ROLE_PRIORITY: Record<string, number> = { owner: 0, admin: 1, user: 2 };

  function profilePriority(p: Profile): number {
    if (!p.roles?.length) return 3;
    const best = Math.min(...p.roles.map((r) => ROLE_PRIORITY[r] ?? Infinity));
    return best < Infinity ? best : 3;
  }

  function profileRoleLabel(p: Profile): string {
    for (const r of TEAM_ROLES) {
      if (p.roles?.includes(r)) return r.charAt(0).toUpperCase() + r.slice(1);
    }
    return 'User';
  }

  const sortedProfiles = [...profiles].sort((a, b) => profilePriority(a) - profilePriority(b));

  function handleClientChange(newClientId: string) {
    setSelectedClientId(newClientId);
    setSelectedPropertyId('');
    setSelectedValue('');
    setCreateNew(false);
    setNewAgentName('');
  }

  const canSubmit = createNew
    ? !!newAgentName.trim() && !!selectedPropertyId
    : !!selectedValue && !!selectedPropertyId;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmit) return;

    setError('');
    setLoading(true);
    try {
      if (createNew) {
        await createAndAssociateAgent(callId, newAgentName.trim(), selectedPropertyId);
      } else if (selectedValue.startsWith('profile:')) {
        const profileId = selectedValue.replace('profile:', '');
        const profile = profiles.find((p) => p.id === profileId);
        if (profile?.agent) {
          await associateAgent(callId, profile.agent.id, selectedPropertyId);
        } else if (profile) {
          await createAndAssociateAgent(callId, profile.displayName, selectedPropertyId, profile.id);
        }
      } else if (selectedValue.startsWith('agent:')) {
        const agentId = selectedValue.replace('agent:', '');
        await associateAgent(callId, agentId, selectedPropertyId);
      }
      onSuccess();
    } catch {
      setError('Failed to associate agent');
    } finally {
      setLoading(false);
    }
  };

  const inputClass = 'w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm';
  const labelClass = 'block text-sm font-medium text-gray-700 mb-1';
  const disabledClass = 'bg-gray-100 cursor-not-allowed';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative bg-white rounded-xl shadow-lg w-full max-w-md mx-4">
        <form onSubmit={handleSubmit}>
          <div className="px-6 pt-6 pb-4">
            <h2 className="text-lg font-bold text-gray-900 mb-4">Associate Agent</h2>
            <div className="space-y-4">
              <div>
                <label className={labelClass}>Client *</label>
                <select
                  value={selectedClientId}
                  onChange={(e) => handleClientChange(e.target.value)}
                  required
                  disabled={locked}
                  className={`${inputClass} ${locked ? disabledClass : ''}`}
                >
                  <option value="">Select a client</option>
                  {clients.map((c) => (
                    <option key={c.id} value={c.id}>{c.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className={labelClass}>Property *</label>
                <select
                  value={selectedPropertyId}
                  onChange={(e) => setSelectedPropertyId(e.target.value)}
                  disabled={locked || !selectedClientId}
                  required
                  className={`${inputClass} ${locked || !selectedClientId ? disabledClass : ''}`}
                >
                  <option value="">{selectedClientId ? 'Select a property' : 'Select a client first'}</option>
                  {properties.map((p) => (
                    <option key={p.id} value={p.id}>{p.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className={labelClass}>Agent *</label>
                {createNew ? (
                  <input
                    type="text"
                    value={newAgentName}
                    onChange={(e) => setNewAgentName(e.target.value)}
                    placeholder="Enter agent name"
                    required
                    className={inputClass}
                  />
                ) : (
                  <select
                    value={selectedValue}
                    onChange={(e) => setSelectedValue(e.target.value)}
                    disabled={!selectedClientId}
                    required={!createNew}
                    className={`${inputClass} ${!selectedClientId ? disabledClass : ''}`}
                  >
                    <option value="">{selectedClientId ? 'Select a user' : 'Select a client first'}</option>
                    {sortedProfiles.map((p) => (
                      <option key={p.id} value={`profile:${p.id}`}>{profileRoleLabel(p)}: {p.displayName}</option>
                    ))}
                    {unlinkedAgents.map((a) => (
                      <option key={a.id} value={`agent:${a.id}`}>Agent: {a.name}</option>
                    ))}
                  </select>
                )}
                <button
                  type="button"
                  onClick={() => {
                    setCreateNew(!createNew);
                    setSelectedValue('');
                    setNewAgentName('');
                  }}
                  className={createNew
                    ? 'text-xs text-blue-600 hover:text-blue-800 mt-1.5'
                    : 'px-3 py-1.5 text-sm font-medium text-blue-600 border border-blue-600 rounded-lg hover:bg-blue-50 transition-colors mt-2'
                  }
                >
                  {createNew ? 'Select existing user' : 'Create New Agent'}
                </button>
              </div>
            </div>
            {error && <p className="text-red-600 text-sm mt-2">{error}</p>}
          </div>
          <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-100">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading || !canSubmit}
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {loading ? 'Associating...' : 'Associate Agent'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
